<script setup>
const emits = defineEmits(['update:modelValue'])
const props = defineProps({
  modelValue: { type: Boolean, default: false }
})
const { $dayjs } = useNuxtApp()
const account = useState('account')
const dialog = ref(props.modelValue)
const edit = ref(false)
const { loading } = useLoading()
const { getAccount, updateAccount } = useDatabase()
const { notification } = useNotifications()

const input = ref({})

const onSave = () => {
  if (confirm('入力した情報に更新します。\nよろしいですか？')) {
    loading.start()
    updateAccount(input.value).then(resp1 => {
      getAccount().then(resp2 => {
        account.value = resp2
        edit.value = false
        notification.success('会員情報が更新されました。', 3)
      })
    }).catch(e => {
      console.error(e)
      notification.error('会員情報を更新できませんでした。', 3)
    }).finally(_ => {
      loading.stop()
    })
  }
}

const zipcodeAutoHyphen = (zipcode) => {
  zipcode = zipcode ? zipcode.replace(/-/g, '') : ''
  if (zipcode.length === 8) zipcode = zipcode.slice(0, 3)
  return zipcode.length ? zipcode.slice(0, 3) + '-' + zipcode.slice(3,zipcode.length) : zipcode;
}
watch(() => edit.value, aft => input.value = {...account.value})
watch(() => props.modelValue, aft => dialog.value = aft)
watch(() => dialog.value, aft => {
  edit.value = false
  emits('update:modelValue', aft)
})
</script>

<template>
  <div>
    <slot></slot>

    <default-ui-dialog
      v-model="dialog"
      title-center
      content-center
      :is-form="edit"
      :width="600"
      @submit="onSave"
    >
      <template #title>会員情報</template>

      <div v-if="!edit">
        <p class="text-right">
          <button
            type="button"
            class="btn btn-sm btn-primary btn-outlined"
            @click="edit = true"
          >
            <icon icon="mdi:account-edit" width="20" />
            編集
          </button>
        </p>

        <table class="table-list w-full">
          <tbody>
            <tr>
              <th class="w-full sm:w-40 pr-0 sm:pr-3">
                <span class="badge badge-lg badge-secondary w-full font-noto-serif">名前</span>
              </th>
              <td class="pl-0 sm:pl-3">{{ account?.last_name }} {{ account?.first_name }}</td>
            </tr>
            <tr>
              <th class="w-full sm:w-40 pr-0 sm:pr-3">
                <span class="badge badge-lg badge-secondary w-full font-noto-serif">名前(カナ)</span>
              </th>
              <td class="pl-0 sm:pl-3">{{ account?.last_name_kana }} {{ account?.first_name_kana }}</td>
            </tr>
            <tr>
              <th class="w-full sm:w-40 pr-0 sm:pr-3">
                <span class="badge badge-lg badge-secondary w-full font-noto-serif">生年月日</span>
              </th>
              <td class="pl-0 sm:pl-3">{{ account?.birthday ? $dayjs(account?.birthday).format('YYYY年M月D日') : '' }}</td>
            </tr>
            <tr>
              <th class="w-full sm:w-40 pr-0 sm:pr-3">
                <span class="badge badge-lg badge-secondary w-full font-noto-serif">性別</span>
              </th>
              <td class="pl-0 sm:pl-3">{{ account?.gender }}</td>
            </tr>
            <tr>
              <th class="w-full sm:w-40 pr-0 sm:pr-3">
                <span class="badge badge-lg badge-secondary w-full font-noto-serif">郵便番号</span>
              </th>
              <td class="pl-0 sm:pl-3">{{ account?.zipcode }}</td>
            </tr>
            <tr>
              <th class="w-full sm:w-40 pr-0 sm:pr-3">
                <span class="badge badge-lg badge-secondary w-full font-noto-serif">住所</span>
              </th>
              <td class="pl-0 sm:pl-3">{{ account?.prefecture+account?.address1+account?.address2 || '' }}<br v-if="account.building">{{  account.building }}</td>
            </tr>
            <tr>
              <th class="w-full sm:w-40 pr-0 sm:pr-3">
                <span class="badge badge-lg badge-secondary w-full font-noto-serif">電話番号</span>
              </th>
              <td class="pl-0 sm:pl-3">{{ account?.phone }}</td>
            </tr>
            <tr>
              <th class="w-full sm:w-40 pr-0 sm:pr-3">
                <span class="badge badge-lg badge-secondary w-full font-noto-serif">メールアドレス</span>
              </th>
              <td class="pl-0 sm:pl-3">{{ account?.email }}</td>
            </tr>
          </tbody>
        </table>

        <p class="text-right mt-3">
          <NuxtLink
            to="/account/withdrawal"
            class="link text-red inline-flex items-center"
          >
            <icon icon="mdi:logout" width="16" />
            退会はこちら
          </NuxtLink>
        </p>
      </div>

      <table v-else class="table-list w-full">
        <tbody>
          <tr>
            <th class="w-full sm:w-40 pr-0 sm:pr-3">
              <span class="mb-1 badge badge-lg badge-secondary w-full font-noto-serif relative">
                <span class="absolute badge badge-red badge-outlined bottom-1/2 sm:-bottom-6 right-2 sm:right-0 translate-y-1/2 sm:-translate-y-0 !px-2">必須</span>
                名前
              </span>
            </th>
            <td class="pl-0 sm:pl-3">
              <div class="input-group">
                <input class="input w-32" required type="text" v-model="input.last_name" />
                <input class="input w-32" required type="text" v-model="input.first_name" />
              </div>
            </td>
          </tr>
          <tr>
            <th class="w-full sm:w-40 pr-0 sm:pr-3">
              <span class="mb-1 badge badge-lg badge-secondary w-full font-noto-serif relative">
                <span class="absolute badge badge-red badge-outlined bottom-1/2 sm:-bottom-6 right-2 sm:right-0 translate-y-1/2 sm:-translate-y-0 !px-2">必須</span>
                名前(カナ)
              </span>
            </th>
            <td class="pl-0 sm:pl-3">
              <div class="input-group">
                <input class="input w-32" required type="text" v-model="input.last_name_kana" />
                <input class="input w-32" required type="text" v-model="input.first_name_kana" />
              </div>
            </td>
          </tr>
          <tr>
            <th class="w-full sm:w-40 pr-0 sm:pr-3">
              <span class="mb-1 badge badge-lg badge-secondary w-full font-noto-serif relative">
                <span class="absolute badge badge-red badge-outlined bottom-1/2 sm:-bottom-6 right-2 sm:right-0 translate-y-1/2 sm:-translate-y-0 !px-2">必須</span>
                生年月日
              </span>
            </th>
            <td class="pl-0 sm:pl-3">
              <input
                required
                class="input"
                type="date"
                :value="$dayjs(input.birthday).format('YYYY-MM-DD')"
                @change="input.birthday = $dayjs($event.target.value).toISOString()"
              /></td>
          </tr>
          <tr>
            <th class="w-full sm:w-40 pr-0 sm:pr-3">
              <span class="mb-1 badge badge-lg badge-secondary w-full font-noto-serif relative">
                <span class="absolute badge badge-red badge-outlined bottom-1/2 sm:-bottom-6 right-2 sm:right-0 translate-y-1/2 sm:-translate-y-0 !px-2">必須</span>
                性別
              </span>
            </th>
            <td class="pl-0 sm:pl-3">
              <select class="select" required v-model="input.gender">
                <option value="男性">男性</option>
                <option value="女性">女性</option>
                <option value="－">未回答</option>
              </select>
            </td>
          </tr>
          <tr>
            <th class="w-full sm:w-40 pr-0 sm:pr-3">
              <span class="mb-1 badge badge-lg badge-secondary w-full font-noto-serif relative">
                <span class="absolute badge badge-red badge-outlined bottom-1/2 sm:-bottom-6 right-2 sm:right-0 translate-y-1/2 sm:-translate-y-0 !px-2">必須</span>
                郵便番号
              </span>
            </th>
            <td class="pl-0 sm:pl-3">
              <div class="input-group">
                <label class="input-group-label">〒</label>
                <input
                  v-model="input.zipcode"
                  required
                  class="input w-28"
                  type="text"
                  minlength="8"
                  maxlength="8"
                  @change="input.zipcode = zipcodeAutoHyphen($event.target.value)"
                />
              </div>
            </td>
          </tr>
          <tr>
            <th class="w-full sm:w-40 pr-0 sm:pr-3">
              <span class="mb-1 badge badge-lg badge-secondary w-full font-noto-serif relative">
                <span class="absolute badge badge-red badge-outlined bottom-1/2 sm:-bottom-6 right-2 sm:right-0 translate-y-1/2 sm:-translate-y-0 !px-2">必須</span>
                住所
              </span>
            </th>
            <td class="pl-0 sm:pl-3">
              <input class="input mb-1 w-full" required type="text" placeholder="都道府県" v-model="input.prefecture" />
              <input class="input mb-1 w-full" required type="text" placeholder="市区町村" v-model="input.address1" />
              <input class="input mb-1 w-full" required type="text" placeholder="町名・番地" v-model="input.address2" />
              <input class="input mb-1 w-full" type="text" placeholder="アパート・マンション" v-model="input.building" />
            </td>
          </tr>
          <tr>
            <th class="w-full sm:w-40 pr-0 sm:pr-3">
              <span class="mb-1 badge badge-lg badge-secondary w-full font-noto-serif relative">
                <span class="absolute badge badge-red badge-outlined bottom-1/2 sm:-bottom-6 right-2 sm:right-0 translate-y-1/2 sm:-translate-y-0 !px-2">必須</span>
                電話番号
              </span>
            </th>
            <td class="pl-0 sm:pl-3">
              <input class="input" required type="text" v-model="input.phone" />
            </td>
          </tr>
          <tr>
            <th class="w-full sm:w-40 pr-0 sm:pr-3">
              <span class="mb-1 badge badge-lg badge-secondary w-full font-noto-serif relative">
                <span class="absolute bottom-1/2 sm:-bottom-5 font-bold right-2 sm:right-0 translate-y-1/2 sm:-translate-y-0 text-xs text-white sm:text-red">変更不可</span>
                メールアドレス
              </span>
            </th>
            <td class="pl-0 sm:pl-3">
              <input class="input w-full" required type="text" disabled :value="input.email" />
            </td>
          </tr>
        </tbody>
      </table>


      <template v-if="edit" #footer>
      <p
        class="inline-flex items-center justify-center gap-x-6"
      >
        <button
          type="button"
          class="btn btn-sm btn-zinc btn-outlined"
          @click="edit = false"
        >
          キャンセル
        </button>

        <button
          type="submit"
          class="btn btn-sm btn-primary"
        >
          <icon icon="mdi:content-save" width="20" />
          保存
        </button>
      </p>
      </template>
    </default-ui-dialog>
  </div>
</template>